"use client";

import { cn } from "@/lib/utils";
import React from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { Button } from "@/components/ui/button";
import Image from '@/components/ui/Image';
import parse from "html-react-parser";
import Link from "next/link";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { SectionWrapper } from "../components/SectionWraper";
import AutoScroll from "embla-carousel-auto-scroll";

interface GalleryItem {
  image?: string;
  imageAltText?: string;
}

interface Gallery6Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  title?: string;
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  galleryItems?: GalleryItem[];
  attributes?: React.HTMLProps<HTMLElement>;
}

const Gallery6 = ({
  darkMode = false,
  variant = "A",
  spacing = "xlarge",
  usedInPageNav = false,
  pageNavTitle = "Gallery 6",
  themeChangeOnScroll = false,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  galleryItems,
  attributes,
}: Gallery6Props) => {

  // Ensure we have enough images for continuous scrolling
  const getImagesForCarousel = (): GalleryItem[] => {
    if (!galleryItems || galleryItems.length === 0) {
      return [];
    }

    // If we have fewer than 30 images, duplicate them to ensure continuous scrolling
    const minImages = 30;
    const neededImages: GalleryItem[] = [];
    
    while (neededImages.length < minImages) {
      neededImages.push(...galleryItems.map(item => ({
        image: item.image || "",
        imageAltText: item.imageAltText || "Gallery image"
      })));
    }
    
    return neededImages;
  };

  const carouselImages = getImagesForCarousel();

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="gallery6"
      attributes={attributes}
      className="overflow-hidden"
    >   
      <div className="container" data-aos="fade-up">
        <div className="flex flex-col md:flex-row gap-x-20 gap-y-6 text-center sm:text-left">
          <div className="w-full md:w-[50%]">
            {title && (
              <>
              <h2>
                {parse(title)}
              </h2>
              </>
            )}
          </div>
          <div className="w-full md:w-[50%]">
            {content && (
              <div className="paragraph-lg mx-auto !font-normal">
                {parse(content)}
              </div>
            )}
            {hasButton && (
              <div className="mt-8 flex flex-col">
                <div className="flex flex-col gap-2 sm:flex-row justify-center xl:justify-start">
                  <Button 
                    size="lg" 
                    variant={buttonStyle || "primary"}
                    className="w-full gap-2 sm:w-auto"
                    asChild
                  >
                    <Link href={buttonLink ?? ""}>
                      {buttonText}
                    </Link>
                  </Button>
                </div>
                {microCopy && (
                  <p className="paragraph-sm text-muted-foreground text-center xl:text-left mt-3">
                    {microCopy}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="w-full mt-8 lg:mt-32">
        <div className="max-w-[100vw] overflow-x-hidden" data-aos="fade-left" data-aos-delay={200}>
          <Carousel
            opts={{
              loop: true,
              align: "start",
              skipSnaps: false,
            }}
            plugins={[
              AutoScroll({
                speed: 0.9,
                playOnInit: true,
                stopOnInteraction: false,
              }),
            ]}
            className="pointer-events-none"
          >
            <CarouselContent className={cn(
              "mr-4 ml-0",
              "md:mr-8 md:ml-3",
              "2xl:mr-[max(0rem,calc(100vw/2-1336px/2))] 2xl:ml-[max(0rem,calc(100vw/2-1336px/2-20px))]"
            )}>
              {carouselImages.map((image, index) => (
                <CarouselItem key={index} className="basis-auto">
                  <div className="max-w-60 max-h-60 md:max-h-94 md:max-w-94">
                    <Image
                      src={image.image || "/placeholder.svg"}
                      alt={image.imageAltText || ""}
                      title={image.imageAltText || ""}
                      className={cn(
                        "mt-7 h-full w-full rounded-2xl object-cover aspect-[1/1]",
                        index % 2 === 0 && "mt-24",
                      )}
                      width={376}
                      height={376}
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>
      </div>
    </SectionWrapper>
  );
};

export { Gallery6 };
